import { schemaResolver, type UseFormReturnType, useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useEffect } from 'react';
import { hasAtLeast, keys } from 'remeda';
import { ClientFormSchema } from 'shared/hooks/use-create-client';
import type { Debtor } from 'types/store/client-state';

import type { PhoneNumber } from '../Form/ClientMultiPhoneInput';
import type { EmailAddress } from '../Form/MultiEmailInputDefault';
import type { IbanBicPair } from '../Form/MultiIbanBicInput';

export type AddressAttributes = {
  countryCode: string | null;
  city: string;
  zipCode: string;
  streetName: string;
  streetNumber: string;
  streetBox: string;
  additionalLine1: string;
  additionalLine2: string;
};

export type ClientFormValues = {
  fullName: string;
  moralEntity: boolean;
  locale: string | null;
  vatNumber: string;
  reference: string;
  currency: string | null;
  coContractor: boolean;
  intracom: boolean;
  addressAttributes: AddressAttributes;
  emailsAttributes: Array<string>;
  emails: Array<EmailAddress>;
  phones: Array<PhoneNumber>;
  bankAccounts: Array<IbanBicPair>;
};

export type ClientForm = UseFormReturnType<ClientFormValues>;

const smoothScrollToFormError = (element: HTMLElement) => {
  element.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  });
};

const initializeEmails = (client: Debtor) => {
  if (hasAtLeast(client.emails, 1)) {
    return client.emails.map((email, index) => ({
      email,
      isDefault: index === 0,
    }));
  }
  return [{ email: '', isDefault: true }];
};

const initializePhones = (client: Debtor) => {
  if (hasAtLeast(client.phones_attributes, 1)) {
    return client.phones_attributes.map(phone => ({
      id: phone.id?.toString(),
      number: phone.number,
    }));
  }
  return [{ number: '' }];
};

const initializeBankAccounts = (client: Debtor) => {
  if (hasAtLeast(client.debtor_bank_accounts_attributes, 1)) {
    return client.debtor_bank_accounts_attributes.map(account => ({
      id: account.id?.toString(),
      iban: account.iban || '',
      bic: account.bic || '',
    }));
  }
  return [{ iban: '', bic: '' }];
};

export const useClientForm = (client?: Debtor) => {
  const { t } = useTranslation();

  const fallback = {
    fullName: '',
    moralEntity: false,
    locale: null,
    vatNumber: '',
    reference: '',
    currency: null,
    coContractor: false,
    intracom: false,
    addressAttributes: {
      countryCode: null,
      city: '',
      zipCode: '',
      streetName: '',
      streetNumber: '',
      streetBox: '',
      additionalLine1: '',
      additionalLine2: '',
    },
    emailsAttributes: [],
    emails: [{ email: '', isDefault: true }],
    phones: [{ number: '' }],
    bankAccounts: [{ iban: '', bic: '' }],
  };

  const form = useForm<ClientFormValues>({
    initialValues: fallback,
    validate: schemaResolver(ClientFormSchema, { sync: true }),
  });

  useEffect(() => {
    if (client) {
      const clientFormValues = {
        fullName: client.full_name,
        moralEntity: client.moral_entity,
        locale: client.locale,
        vatNumber: client.vat_number ?? '',
        reference: client.reference ?? '',
        currency: client.currency ?? null,
        coContractor: client.co_contractor ?? false,
        intracom: client.intracom ?? false,
        addressAttributes: {
          countryCode: client.address_attributes?.country_code ?? null,
          city: client.address_attributes?.city ?? '',
          zipCode: client.address_attributes?.zip_code ?? '',
          streetName: client.address_attributes?.street_name ?? '',
          streetNumber: client.address_attributes?.street_number?.toString() ?? '',
          streetBox: client.address_attributes?.street_box ?? '',
          additionalLine1: client.address_attributes?.additional_line_1 ?? '',
          additionalLine2: client.address_attributes?.additional_line_2 ?? '',
        },
        emailsAttributes: client.emails,
        emails: initializeEmails(client),
        phones: initializePhones(client),
        bankAccounts: initializeBankAccounts(client),
      };

      form.setValues(clientFormValues);
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);

  const handleFormErrors = (errors: typeof form.errors) => {
    const errorKeys = keys(errors);
    const errorElement = form.getInputNode(errorKeys[0]);

    if (errorElement) {
      smoothScrollToFormError(errorElement);
    }

    const hasEmailErrors = errorKeys.some(
      key => key.startsWith('emails.') && key.includes('.email'),
    );
    const hasPhoneErrors = errorKeys.some(
      key => key.startsWith('phones.') && key.includes('.number'),
    );
    const hasBankAccountErrors = errorKeys.some(
      key => key.startsWith('bankAccounts.') && key.includes('.iban'),
    );

    if (hasEmailErrors)
      notifications.show({
        title: t(i18nKeys.ERROR.ERROR),
        message: t(i18nKeys.ERROR.INVALID_EMAIL),
        color: 'red',
      });

    if (hasPhoneErrors)
      notifications.show({
        title: t(i18nKeys.ERROR.ERROR),
        message: t(i18nKeys.ERROR.INVALID_PHONE),
        color: 'red',
      });

    if (hasBankAccountErrors)
      notifications.show({
        title: t(i18nKeys.ERROR.ERROR),
        message: t(i18nKeys.ERROR.INVALID_IBAN),
        color: 'red',
      });
  };

  return { form, handleFormErrors };
};
